import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, get, patch, post, put, type Role } from "../../lib/api";
import { formatBytes, formatDate, RETENTION_LABELS } from "../../lib/format";
import { Alert, Badge, Button, Card, Field, Input, Loading, Modal, PageHeader, Select, Table, Tabs, Td } from "../../components/ui";
import { can, useWorkspace, useWorkspaceId } from "./hooks";

type Tab = "general" | "retention" | "members" | "audit";

export function SettingsPage() {
  const ws = useWorkspaceId();
  const detail = useWorkspace(ws);
  const [tab, setTab] = useState<Tab>("general");
  if (detail.isLoading) return <Loading />;
  const perms = detail.data?.permissions;
  return (
    <>
      <PageHeader title="Settings" />
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "general", label: "General" },
          { value: "retention", label: "Data retention" },
          { value: "members", label: "Members" },
          ...(can(perms, "ws.manage") ? [{ value: "audit" as const, label: "Audit log" }] : []),
        ]}
      />
      {tab === "general" && <General ws={ws} />}
      {tab === "retention" && <Retention ws={ws} />}
      {tab === "members" && <Members ws={ws} />}
      {tab === "audit" && <Audit ws={ws} />}
    </>
  );
}

function General({ ws }: { ws: string }) {
  const qc = useQueryClient();
  const d = useWorkspace(ws).data!;
  const [name, setName] = useState(d.workspace.name);
  const [logo, setLogo] = useState(d.workspace.logoUrl ?? "");
  const [msg, setMsg] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const editable = can(d.permissions, "ws.manage");
  return (
    <Card title="Workspace">
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <Field label="Name" htmlFor="wsname">
        <Input id="wsname" disabled={!editable} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Logo URL" htmlFor="logo" hint="Shown on client-facing migration reports (https only).">
        <Input id="logo" disabled={!editable} placeholder="https://…" value={logo} onChange={(e) => setLogo(e.target.value)} />
      </Field>
      <dl className="muted mb-4 grid grid-cols-2 gap-2 text-sm">
        <dt>Region</dt>
        <dd>{d.workspace.region.toUpperCase()} (fixed at creation)</dd>
        <dt>Workspace ID</dt>
        <dd className="font-mono text-xs">{d.workspace.id}</dd>
      </dl>
      {editable && (
        <Button
          onClick={async () => {
            try {
              await patch(`/api/workspaces/${ws}`, { name, logoUrl: logo || null });
              setMsg({ tone: "ok", text: "Saved." });
              qc.invalidateQueries({ queryKey: ["ws", ws] });
              qc.invalidateQueries({ queryKey: ["me"] });
            } catch (e) {
              setMsg({ tone: "danger", text: errorMessage(e) });
            }
          }}
        >
          Save
        </Button>
      )}
    </Card>
  );
}

function Retention({ ws }: { ws: string }) {
  const qc = useQueryClient();
  const d = useWorkspace(ws).data!;
  const [choice, setChoice] = useState(d.workspace.retention);
  const [confirm, setConfirm] = useState<{ chunks: number; files: number; reports: number; bytes: number } | null>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const editable = can(d.permissions, "ws.manage");

  const save = async (confirmed: boolean) => {
    try {
      await put(`/api/workspaces/${ws}/retention`, { retention: choice, confirm: confirmed });
      setConfirm(null);
      setMsg({ tone: "ok", text: `Retention set to ${RETENTION_LABELS[choice]}.` });
      qc.invalidateQueries({ queryKey: ["ws", ws] });
    } catch (e) {
      const err = e as { code?: string };
      if (err.code === "confirmation_required") setConfirm(await get(`/api/workspaces/${ws}/retention/preview?retention=${choice}`));
      else setMsg({ tone: "danger", text: errorMessage(e) });
    }
  };

  return (
    <Card title="How long we keep your migration data">
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <p className="muted mb-4 text-sm">
        Applies to staged records, pre-change snapshots (which make revert possible) and reports. Run history, ID links and the audit log are always kept. Storage is billed per GB.
      </p>
      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        {Object.entries(RETENTION_LABELS).map(([k, label]) => (
          <label key={k} className={`focus-ring flex cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm ${choice === k ? "border-accent bg-accent-soft" : "border-default"}`}>
            <input type="radio" name="retention" value={k} disabled={!editable} checked={choice === k} onChange={() => setChoice(k)} />
            {label}
          </label>
        ))}
      </div>
      {choice === "transit" && <Alert tone="warn">Transit only: data is deleted as soon as a run finishes, so a run can't be reverted after it completes.</Alert>}
      {editable && (
        <Button disabled={choice === d.workspace.retention} onClick={() => save(false)}>
          Save
        </Button>
      )}
      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title="Shorten data retention?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => save(true)}>
              Shorten retention
            </Button>
          </>
        }
      >
        {confirm && (
          <p className="text-sm">
            The next nightly purge will delete {confirm.chunks} data chunks, {confirm.files} files and {confirm.reports} reports ({formatBytes(confirm.bytes)}). Runs older than the new window can no longer be reverted. This can't be undone.
          </p>
        )}
      </Modal>
    </Card>
  );
}

interface MembersData {
  members: Array<{ id: string; name: string; email: string; role: Role; created_at: number }>;
  invites: Array<{ id: string; email: string; role: Role; expires_at: number }>;
}

const ROLES: Role[] = ["owner", "admin", "operator", "viewer", "billing"];
const ROLE_HELP: Record<Role, string> = {
  owner: "Everything, including deleting the workspace and shortening retention",
  admin: "Everything except deleting the workspace",
  operator: "Manage connections and run migrations",
  viewer: "Read-only",
  billing: "Credits and invoices only",
};

function Members({ ws }: { ws: string }) {
  const qc = useQueryClient();
  const d = useWorkspace(ws).data!;
  const members = useQuery({ queryKey: ["members", ws], queryFn: () => get<MembersData>(`/api/workspaces/${ws}/members`) });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("operator");
  const [msg, setMsg] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const manage = can(d.permissions, "members.manage");
  const refresh = () => qc.invalidateQueries({ queryKey: ["members", ws] });
  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    try {
      await fn();
      if (ok) setMsg({ tone: "ok", text: ok });
      refresh();
    } catch (e) {
      setMsg({ tone: "danger", text: errorMessage(e) });
    }
  };
  if (members.isLoading) return <Loading />;
  return (
    <>
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      {manage && (
        <Card title="Invite someone" className="mb-6">
          <form
            className="grid gap-3 sm:grid-cols-[1fr_180px_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              act(() => post(`/api/workspaces/${ws}/invites`, { email, role }), `Invite sent to ${email}.`).then(() => setEmail(""));
            }}
          >
            <Input type="email" required placeholder="name@company.com" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
            <Button type="submit">Invite</Button>
          </form>
          <p className="muted mt-2 text-xs">{ROLE_HELP[role]}.</p>
        </Card>
      )}
      <Card title="Members">
        <Table head={["Name", "Email", "Role", ""]}>
          {members.data!.members.map((m) => (
            <tr key={m.id}>
              <Td>{m.name}</Td>
              <Td className="muted">{m.email}</Td>
              <Td>
                {manage ? (
                  <Select aria-label={`Role for ${m.email}`} value={m.role} onChange={(e) => act(() => patch(`/api/workspaces/${ws}/members/${m.id}`, { role: e.target.value }))}>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Badge>{m.role}</Badge>
                )}
              </Td>
              <Td className="text-right">
                {manage && (
                  <Button size="sm" variant="ghost" onClick={() => confirm(`Remove ${m.email}?`) && act(() => del(`/api/workspaces/${ws}/members/${m.id}`))}>
                    Remove
                  </Button>
                )}
              </Td>
            </tr>
          ))}
          {members.data!.invites.map((i) => (
            <tr key={i.id}>
              <Td className="muted">Invited</Td>
              <Td className="muted">{i.email}</Td>
              <Td>
                <Badge>{i.role}</Badge>
              </Td>
              <Td className="text-right">
                {manage && (
                  <Button size="sm" variant="ghost" onClick={() => act(() => del(`/api/workspaces/${ws}/invites/${i.id}`))}>
                    Cancel invite
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
        {d.workspace.partner && <p className="muted mt-3 text-xs">Members of {d.workspace.partner.name} also have access through the partner account.</p>}
      </Card>
    </>
  );
}

function Audit({ ws }: { ws: string }) {
  const audit = useQuery({
    queryKey: ["audit", ws],
    queryFn: () => get<{ entries: Array<{ at: number; action: string; target: string | null; ip: string | null; actor_email: string | null; actor_type: string; details: string | null }> }>(`/api/workspaces/${ws}/audit`),
  });
  if (audit.isLoading) return <Loading />;
  return (
    <Card title="Audit log">
      <Table head={["When", "Who", "Action", "Target", "Details"]}>
        {audit.data!.entries.map((e, i) => (
          <tr key={i}>
            <Td className="muted whitespace-nowrap">{formatDate(e.at)}</Td>
            <Td>{e.actor_email ?? e.actor_type}</Td>
            <Td>
              <Badge>{e.action}</Badge>
            </Td>
            <Td className="font-mono text-xs">{e.target ?? ""}</Td>
            <Td className="muted max-w-xs truncate font-mono text-xs">{e.details ?? ""}</Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}
